import { Link } from 'react-router-dom';
import { ArrowLeft, Calendar, Gift, HandHeart } from 'lucide-react';

export default function ArtikelZakatSadaqah() {
  return (
    <main className="pt-20 md:pt-24 bg-[#FDFCF8]">
      <section className="py-16 md:py-24 bg-[#4A5D4F]">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <Link to="/ratgeber" className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-8 transition-colors"><ArrowLeft className="w-4 h-4" />Alle Ratgeber</Link>
          <div className="flex items-center gap-2 text-[#D4C5A9] text-sm mb-4"><Calendar className="w-4 h-4" /><span>Februar 2026</span></div>
          <h1 className="font-serif text-3xl md:text-4xl lg:text-5xl font-medium text-white mb-6 leading-tight">Zakat und Sadaqah: Geben und Schenken im Islam</h1>
          <p className="text-lg text-white/80 leading-relaxed">Was ist der Unterschied zwischen Zakat und Sadaqah? Wie viel sollte man geben? Ein umfassender Guide zum Thema Spenden im Islam.</p>
        </div>
      </section>
      <article className="py-16 md:py-20">
        <div className="max-w-3xl mx-auto px-6 md:px-12 prose prose-lg max-w-none">
          <p className="text-xl text-[#2C332E] leading-relaxed mb-8">Geben ist ein zentraler Wert im Islam. Ob Zakat (die Pflichtabgabe), Sadaqah (freiwillige Spende) oder Zakat al-Fitr (die Spende zum Fastenbrechen) – jede Form des Gebens hat ihren eigenen Stellenwert und ihre eigenen Regeln. Besonders im Ramadan und zu Eid ist das Thema Spenden allgegenwärtig.</p>

          <section className="mb-12">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-full bg-[#D4C5A9]/30 flex items-center justify-center"><HandHeart className="w-6 h-6 text-[#4A5D4F]" /></div>
              <h2 className="font-serif text-2xl font-medium text-[#2C332E]">Zakat – Die Pflichtabgabe</h2>
            </div>
            <div className="space-y-4">
              <div className="bg-[#F5F5F0] rounded-xl p-5">
                <h4 className="font-medium text-[#2C332E] mb-2">Was ist Zakat?</h4>
                <p className="text-sm text-[#4B5563]">Zakat ist die dritte Säule des Islam und eine Pflichtabgabe für jeden Muslim, der über dem Nisab (Mindestbetrag) liegt. Es sind 2,5% des gesamten Vermögens, das ein Jahr lang besessen wurde.</p>
              </div>
              <div className="bg-[#F5F5F0] rounded-xl p-5">
                <h4 className="font-medium text-[#2C332E] mb-2">Wer muss Zakat zahlen?</h4>
                <p className="text-sm text-[#4B5563]">Jeder erwachsene Muslim, der den Nisab erreicht (ca. 85g Gold oder 595g Silber Gegenwert) und dieses Vermögen ein Mondjahr lang besitzt. Der Nisab liegt 2026 bei ca. 5.000-6.000€ (schwankt mit dem Goldpreis).</p>
              </div>
              <div className="bg-[#F5F5F0] rounded-xl p-5">
                <h4 className="font-medium text-[#2C332E] mb-2">Worauf wird Zakat berechnet?</h4>
                <p className="text-sm text-[#4B5563]">Bargeld und Bankguthaben, Gold und Silber, Aktien und Investments, Geschäftsvermögen, Mieteinnahmen. Nicht: Eigenheim, Auto, persönliche Gegenstände, Schulden.</p>
              </div>
              <div className="bg-[#F5F5F0] rounded-xl p-5">
                <h4 className="font-medium text-[#2C332E] mb-2">Wer empfängt Zakat?</h4>
                <p className="text-sm text-[#4B5563]">Der Quran nennt acht Kategorien: Arme, Bedürftige, Zakat-Verwalter, Neumuslime, Verschuldete, auf dem Wege Allahs, Reisende und zur Befreiung von Sklaven. Heute wird Zakat hauptsächlich an Arme und Bedürftige verteilt.</p>
              </div>
            </div>
          </section>

          <section className="mb-12">
            <h2 className="font-serif text-2xl font-medium text-[#2C332E] mb-6">Zakat al-Fitr – Die Eid-Spende</h2>
            <div className="space-y-4">
              <div className="bg-[#F5F5F0] rounded-xl p-5">
                <h4 className="font-medium text-[#2C332E] mb-2">Was ist Zakat al-Fitr?</h4>
                <p className="text-sm text-[#4B5563]">Eine obligatorische Spende, die vor dem Eid ul Fitr-Gebet entrichtet wird. Sie soll sicherstellen, dass auch Bedürftige am Fest teilhaben können und ein Festmahl haben.</p>
              </div>
              <div className="bg-[#F5F5F0] rounded-xl p-5">
                <h4 className="font-medium text-[#2C332E] mb-2">Wie viel?</h4>
                <p className="text-sm text-[#4B5563]">Pro Person ca. 10-15€ (der Wert einer Mahlzeit – ca. 2,5 kg Grundnahrungsmittel). Wird für jedes Familienmitglied gezahlt, auch für Kinder und Babys. Der Haushaltsvorstand zahlt für alle.</p>
              </div>
              <div className="bg-[#F5F5F0] rounded-xl p-5">
                <h4 className="font-medium text-[#2C332E] mb-2">Wann zahlen?</h4>
                <p className="text-sm text-[#4B5563]">Muss vor dem Eid-Gebet entrichtet werden. Viele Muslime zahlen in den letzten Tagen des Ramadan über ihre Moschee oder eine Hilfsorganisation.</p>
              </div>
            </div>
          </section>

          <section className="mb-12">
            <h2 className="font-serif text-2xl font-medium text-[#2C332E] mb-6">Sadaqah – Freiwillige Spende</h2>
            <div className="space-y-4">
              <div className="bg-[#F5F5F0] rounded-xl p-5">
                <h4 className="font-medium text-[#2C332E] mb-2">Was ist Sadaqah?</h4>
                <p className="text-sm text-[#4B5563]">Jede freiwillige gute Tat oder Spende. Sadaqah ist nicht auf Geld beschränkt – ein Lächeln, ein gutes Wort, einem Nachbarn helfen oder Wissen teilen zählt ebenfalls als Sadaqah.</p>
              </div>
              <div className="bg-[#F5F5F0] rounded-xl p-5">
                <h4 className="font-medium text-[#2C332E] mb-2">Sadaqah Jariyah</h4>
                <p className="text-sm text-[#4B5563]">Eine besondere Form: Die "fortwährende Spende". Beispiele: Einen Brunnen bauen, Wissen lehren, einen Quran verschenken. Der Lohn fließt auch nach dem Tod weiter – ein beliebtes Geschenkkonzept zu Eid.</p>
              </div>
              <div className="bg-[#F5F5F0] rounded-xl p-5">
                <h4 className="font-medium text-[#2C332E] mb-2">Sadaqah im Ramadan</h4>
                <p className="text-sm text-[#4B5563]">Im Ramadan wird die Belohnung für gute Taten vervielfacht. Deshalb spenden viele Muslime in diesem Monat besonders großzügig. Der Prophet (s.a.s.) war im Ramadan "großzügiger als der Wind".</p>
              </div>
            </div>
          </section>

          <section className="mb-12">
            <h2 className="font-serif text-2xl font-medium text-[#2C332E] mb-6">Schenken als Sadaqah</h2>
            <div className="bg-[#4A5D4F]/10 rounded-xl p-6">
              <p className="text-[#4B5563] mb-4">Auch das Schenken zu Eid ist eine Form von Sadaqah:</p>
              <ul className="text-[#4B5563] space-y-3">
                <li><strong>Geschenke stärken Beziehungen:</strong> Der Prophet (s.a.s.) sagte: "Beschenkt einander, denn Geschenke vertreiben Groll."</li>
                <li><strong>Im Namen eines Verstorbenen:</strong> Eine Spende oder ein Brunnen "im Namen von..." ist ein beliebtes Eid-Geschenk</li>
                <li><strong>Nachbarn beschenken:</strong> Essen und Süßigkeiten mit Nachbarn teilen – egal welcher Religion</li>
                <li><strong>Waisenkinder:</strong> Viele Organisationen bieten "Eid-Geschenk-Pakete" für Waisenkinder an</li>
              </ul>
            </div>
          </section>

          <section className="mb-12">
            <h2 className="font-serif text-2xl font-medium text-[#2C332E] mb-6">Wo spenden in Deutschland?</h2>
            <div className="space-y-4">
              <div className="bg-[#F5F5F0] rounded-xl p-5">
                <h4 className="font-medium text-[#2C332E] mb-2">Lokale Moscheen</h4>
                <p className="text-sm text-[#4B5563]">Direkte Unterstützung der lokalen Gemeinde. Viele Moscheen verteilen Zakat al-Fitr direkt an bedürftige Gemeindemitglieder.</p>
              </div>
              <div className="bg-[#F5F5F0] rounded-xl p-5">
                <h4 className="font-medium text-[#2C332E] mb-2">Islamische Hilfsorganisationen</h4>
                <p className="text-sm text-[#4B5563]">Islamic Relief Deutschland, Muslim Aid, Penny Appeal und andere bieten verschiedene Spendenprojekte an – von Lebensmittelpaketen bis zu Bildungsprogrammen.</p>
              </div>
              <div className="bg-[#F5F5F0] rounded-xl p-5">
                <h4 className="font-medium text-[#2C332E] mb-2">Zakat-Rechner</h4>
                <p className="text-sm text-[#4B5563]">Online-Zakat-Rechner helfen dir, den genauen Betrag zu berechnen. Viele islamische Organisationen bieten solche Tools auf ihren Webseiten an.</p>
              </div>
            </div>
          </section>

          <section className="mb-12">
            <h2 className="font-serif text-2xl font-medium text-[#2C332E] mb-6">Fazit</h2>
            <p className="text-[#4B5563] leading-relaxed mb-4">Geben ist im Islam nicht nur eine Pflicht, sondern eine Freude. Ob durch Zakat, Sadaqah oder das Schenken zu Eid – jede Form des Gebens reinigt das Herz, stärkt die Gemeinschaft und hilft denen, die es am meisten brauchen.</p>
            <p className="text-xl text-[#4A5D4F] font-medium">Möge Allah eure Spenden annehmen!</p>
          </section>

          <div className="bg-[#F5F5F0] rounded-2xl p-6 mb-8">
            <h3 className="font-serif text-lg font-medium text-[#2C332E] mb-4">Weitere Artikel</h3>
            <ul className="space-y-2">
              <li><Link to="/ratgeber/ramadan-fuer-anfaenger" className="text-[#4A5D4F] hover:underline">→ Ramadan für Anfänger</Link></li>
              <li><Link to="/ratgeber/bedeutung-eid-ul-adha" className="text-[#4A5D4F] hover:underline">→ Die Bedeutung von Eid ul Adha</Link></li>
            </ul>
          </div>

          <div className="bg-[#4A5D4F] rounded-2xl p-8 text-center">
            <h3 className="font-serif text-2xl font-medium text-white mb-4">Geschenke entdecken</h3>
            <Link to="/geschenke" className="inline-flex items-center gap-2 bg-white text-[#4A5D4F] px-8 py-3 rounded-full font-medium hover:bg-[#D4C5A9] transition-colors">Jetzt stöbern<Gift className="w-5 h-5" /></Link>
          </div>
        </div>
      </article>
    </main>
  );
}
